import axios from "axios";
import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import CircularProgress from "@mui/material/CircularProgress";
import API_ENDPOINTS from "../config/api";

const Container = styled.main`
  min-height: 100dvh;
  width: 100%;
  padding: 2rem;
  display: flex;
  flex-direction: column;
`;

const TilesSection = styled.section`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2rem;
  margin-top: 2rem;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #013566;
  margin: 0 0 1rem 0;
  text-align: center;
`;

const ActivityBox = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const GoBack = styled.p`
  font-size: 1.4rem;
  font-weight: 500;
  text-decoration: underline;
  cursor: pointer;
  color: #013566;
  margin-top: 2rem;
`;

const TablesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin-top: 2rem;
`;

const TopTablesRow = styled.div`
  display: flex;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const InfoTile = styled.div`
  border: 2px solid #eee;
  border-radius: 12px;
  -webkit-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  -moz-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
`;

const InfoTileHeader = styled.div<{ $color?: string }>`
  width: 100%;
  background: ${(props) => props.$color || "#f8f9fa"};
  padding: 16px 20px;
  border-radius: 8px;
  border: 1px solid #eee;
  margin-bottom: 1rem;

  h2 {
    font-size: 1.4rem;
    font-weight: 700;
    color: white;
    margin: 0;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }
`;

const InfoTileSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid #eee;
  gap: 1rem;
  padding: 8px 0;

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: #013566;
  min-width: 120px;
`;

const InfoValue = styled.span`
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  text-align: right;
  flex: 1;
`;

const formatDateToUS = (dateString: string | null | undefined): string => {
  if (!dateString) return "N/A";

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";

    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const formattedHours = String(hours).padStart(2, "0");

    return `${month}/${day}/${year} ${formattedHours}:${minutes} ${ampm}`;
  } catch (error) {
    return "N/A";
  }
};

const LeadDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const token = localStorage.getItem("token");
  const [leadDetails, setLeadDetails] = useState<any>(null);

  const fetchLeadDetails = async () => {
    if (!id) return;

    const response = await axios.get(`${API_ENDPOINTS.LEAD_DETAILS(id)}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    setLeadDetails(response.data);
  };

  useEffect(() => {
    fetchLeadDetails();
  }, [id, token]);

  console.log(leadDetails);
  return (
    <Container>
      <PageTitle>Lead Details</PageTitle>
      <GoBack onClick={() => navigate("/leads")}>← Back to Leads</GoBack>

      <TablesContainer>
        <TopTablesRow>
          {/* Lead Information Table */}
          <InfoTile>
            <InfoTileHeader $color="#6B8E6B">
              <h2>Lead Information</h2>
            </InfoTileHeader>
            <InfoTileSection>
              <InfoRow>
                <InfoLabel>Date:</InfoLabel>
                <InfoValue>{formatDateToUS(leadDetails?.created_at)}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Full Name:</InfoLabel>
                <InfoValue>
                  {leadDetails?.first_name ? leadDetails?.first_name : "N/A"}{" "}
                  {leadDetails?.last_name ? leadDetails?.last_name : "N/A"}
                </InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Email:</InfoLabel>
                <InfoValue>
                  {leadDetails?.email ? leadDetails?.email : "N/A"}
                </InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Phone:</InfoLabel>
                <InfoValue>
                  {leadDetails?.phone ? leadDetails?.phone : "N/A"}
                </InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Address:</InfoLabel>
                <InfoValue>
                  {leadDetails?.street_address ||
                  leadDetails?.address ||
                  "N/A"}
                </InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>City:</InfoLabel>
                <InfoValue>
                  {leadDetails?.city ? leadDetails?.city : "N/A"}
                </InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>State:</InfoLabel>
                <InfoValue>
                  {leadDetails?.state ? leadDetails?.state : "N/A"}
                </InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>ZIP:</InfoLabel>
                <InfoValue>
                  {leadDetails?.zip ? leadDetails?.zip : "N/A"}
                </InfoValue>
              </InfoRow>
            </InfoTileSection>
          </InfoTile>

          {/* Buyer Information Table */}
          <InfoTile>
            <InfoTileHeader $color="#5A7A9A">
              <h2>Buyer Information</h2>
            </InfoTileHeader>
            <InfoTileSection>
              <InfoRow>
                <InfoLabel>Buyer:</InfoLabel>
                <InfoValue>
                  {leadDetails?.client?.name
                    ? leadDetails?.client?.name
                    : "N/A"}
                </InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Vertical:</InfoLabel>
                <InfoValue>
                  {leadDetails?.campaign?.vertical
                    ? leadDetails?.campaign?.vertical
                    : "N/A"}
                </InfoValue>
              </InfoRow>
            </InfoTileSection>
          </InfoTile>
        </TopTablesRow>

        {/* Tracking Details Table */}
        <InfoTile>
          <InfoTileHeader $color="#B8860B">
            <h2>Tracking Details</h2>
          </InfoTileHeader>
          <InfoTileSection>
            <InfoRow>
              <InfoLabel>Trusted Form:</InfoLabel>
              <InfoValue>
                {leadDetails?.trusted_form_url
                  ? leadDetails?.trusted_form_url
                  : "N/A"}
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Timestamp:</InfoLabel>
              <InfoValue>{formatDateToUS(leadDetails?.created_at)}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Campaign ID:</InfoLabel>
              <InfoValue>
                {leadDetails?.campaign?.cid
                  ? leadDetails?.campaign?.cid
                  : "N/A"}
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Sub ID 1:</InfoLabel>
              <InfoValue>
                {leadDetails?.sub1 ? leadDetails?.sub1 : "N/A"}
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Sub ID 2:</InfoLabel>
              <InfoValue>
                {leadDetails?.sub2 ? leadDetails?.sub2 : "N/A"}
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Sub ID 3:</InfoLabel>
              <InfoValue>{leadDetails?.sub3 || "N/A"}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Transaction ID:</InfoLabel>
              <InfoValue>
                {leadDetails?.transaction_id
                  ? leadDetails?.transaction_id
                  : "N/A"}
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Lead ID:</InfoLabel>
              <InfoValue>
                {leadDetails?.phonexa_response?.lead_id
                  ? leadDetails.phonexa_response.lead_id
                  : "N/A"}
              </InfoValue>
            </InfoRow>
          </InfoTileSection>
        </InfoTile>
      </TablesContainer>
    </Container>
  );
};

export default LeadDetails;
